<script setup lang="ts">
import SmallModel from '@/components/Interface/Modal/ModelSizes/SmallModel.vue'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import SelectSearch from '@/components/Elements/Forms/SelectSearch.vue'
import FormInput from '@/components/Interface/Modal/Forms/FormInput.vue'
import FormSelect from '@/components/Elements/Forms/FormSelect.vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import { useGameStore } from '@/stores/gameStore.ts'
import { usePlanetStore } from '@/stores/planetStore.ts'
import { useTableStore } from '@/stores/tableStore.ts'
import { usePlanetGroupStore } from '@/stores/planetGroupStore.ts'
import { useAssetStore } from '@/stores/assetStore.ts'
import { useShipDesignStore } from '@/stores/shipDesignStore.ts'
import { computed, reactive } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const gameStore = useGameStore()
const planetStore = usePlanetStore()
const tableStore = useTableStore()
const planetGroupStore = usePlanetGroupStore()
const assetStore = useAssetStore()
const shipDesignStore = useShipDesignStore()

// Filter state
const filters = reactive({
  compatibilityMode: 'is' as string,
  compatibility: '' as string,
  isProducingMode: 'is' as string,
  isProducing: '' as string,
  planetGroupMode: 'is' as string,
  planetGroup: 0 as number,
  hasBuildingMode: 'has' as string,
  hasBuilding: '' as string,
  planetSizeMode: 'is' as string,
  planetSize: 0 as number,
  planetTypeMode: 'is' as string,
  planetType: 0 as number,
  populationOperator: '' as string,
  populationValue: null as number | null,
  foodOperator: '' as string,
  foodValue: null as number | null,
  productionOperator: '' as string,
  productionValue: null as number | null,
  scienceOperator: '' as string,
  scienceValue: null as number | null,
  distancePlanetId: 0 as number,
  distanceValue: null as number | null
})

// Operator options for FormSelect
const isIsNotOptions = computed(() => [
  { value: 'is', label: t('modals.advancedSelect.operators.is') },
  { value: 'is not', label: t('modals.advancedSelect.operators.isNot') }
])

const hasHasNotOptions = computed(() => [
  { value: 'has', label: t('modals.advancedSelect.operators.has') },
  { value: 'has not', label: t('modals.advancedSelect.operators.hasNot') }
])

const numericOperatorOptions = computed(() => [
  { value: '', label: '-' },
  { value: '>', label: '>' },
  { value: '=', label: '=' },
  { value: '<', label: '<' }
])

// Get player's planets
const playerPlanets = computed(() => {
  return Array.from(planetStore.planets.values()).filter(
    (planet) => planet.playerId === gameStore.player?.id
  )
})

// Get unique buildings across all planets for building filter
const availableBuildings = computed(() => {
  const buildingSet = new Set<string>()
  for (const planet of playerPlanets.value) {
    for (const building of planet.buildings) {
      buildingSet.add(building)
    }
  }
  return Array.from(buildingSet).sort()
})

// Get production items currently being built across all planets
const productionItems = computed(() => {
  const items: { key: string; name: string; type: 'building' | 'ship' }[] = []
  const seenKeys = new Set<string>()

  for (const planet of playerPlanets.value) {
    // Building production
    if (planet.productionObjectId > 0) {
      const key = `building:${planet.productionObjectId}`
      if (!seenKeys.has(key)) {
        seenKeys.add(key)
        const prodObj = assetStore.findProductionObjectById(planet.productionObjectId)
        if (prodObj) {
          const name = prodObj.type === 'building'
            ? t(`buildings.buildings.${prodObj.objectId}.name`)
            : prodObj.productionObjectName
          items.push({ key, name, type: 'building' })
        }
      }
    }
    // Ship production
    if (planet.productionShipDesignId > 0) {
      const key = `ship:${planet.productionShipDesignId}`
      if (!seenKeys.has(key)) {
        seenKeys.add(key)
        const design = shipDesignStore.getShipDesignById(planet.productionShipDesignId)
        if (design) {
          items.push({ key, name: design.name, type: 'ship' })
        }
      }
    }
  }

  return items.sort((a, b) => a.name.localeCompare(b.name))
})

// Computed filtered planets based on current filter settings
const filteredPlanets = computed(() => {
  return playerPlanets.value.filter((planet) => {
    // Compatibility filter
    if (filters.compatibility) {
      const matches = planet.compatibility === filters.compatibility
      if (filters.compatibilityMode === 'is' && !matches) return false
      if (filters.compatibilityMode === 'is not' && matches) return false
    }

    // Is Producing filter
    if (filters.isProducing) {
      let matches = false
      if (filters.isProducing === 'none') {
        matches = planet.productionObjectId === 0 && planet.productionShipDesignId === 0
      } else if (filters.isProducing.startsWith('building:')) {
        const prodId = Number(filters.isProducing.split(':')[1])
        matches = planet.productionObjectId === prodId
      } else if (filters.isProducing.startsWith('ship:')) {
        const designId = Number(filters.isProducing.split(':')[1])
        matches = planet.productionShipDesignId === designId
      }
      if (filters.isProducingMode === 'is' && !matches) return false
      if (filters.isProducingMode === 'is not' && matches) return false
    }

    // Planet group filter
    if (filters.planetGroup !== 0) {
      let matches = false
      if (filters.planetGroup === -1) {
        matches = planet.planetGroupId === 0
      } else {
        matches = planet.planetGroupId === filters.planetGroup
      }
      if (filters.planetGroupMode === 'is' && !matches) return false
      if (filters.planetGroupMode === 'is not' && matches) return false
    }

    // Building filter
    if (filters.hasBuilding) {
      const matches = planet.buildings.includes(filters.hasBuilding)
      if (filters.hasBuildingMode === 'has' && !matches) return false
      if (filters.hasBuildingMode === 'has not' && matches) return false
    }

    // Planet size filter
    if (filters.planetSize > 0) {
      const matches = planet.size === filters.planetSize
      if (filters.planetSizeMode === 'is' && !matches) return false
      if (filters.planetSizeMode === 'is not' && matches) return false
    }

    // Planet type filter
    if (filters.planetType > 0) {
      const matches = Number(planet.type) === filters.planetType
      if (filters.planetTypeMode === 'is' && !matches) return false
      if (filters.planetTypeMode === 'is not' && matches) return false
    }

    // Population filter
    if (filters.populationOperator && filters.populationValue !== null) {
      const pop = planet.population
      const val = filters.populationValue
      if (filters.populationOperator === '>' && pop <= val) return false
      if (filters.populationOperator === '=' && pop !== val) return false
      if (filters.populationOperator === '<' && pop >= val) return false
    }

    // Food filter (population growth per turn)
    if (filters.foodOperator && filters.foodValue !== null) {
      const food = planet.populationGrowthPerTurn
      const val = filters.foodValue
      if (filters.foodOperator === '>' && food <= val) return false
      if (filters.foodOperator === '=' && food !== val) return false
      if (filters.foodOperator === '<' && food >= val) return false
    }

    // Production filter (production per turn)
    if (filters.productionOperator && filters.productionValue !== null) {
      const prod = planet.productionPerTurn
      const val = filters.productionValue
      if (filters.productionOperator === '>' && prod <= val) return false
      if (filters.productionOperator === '=' && prod !== val) return false
      if (filters.productionOperator === '<' && prod >= val) return false
    }

    // Science filter (science per turn)
    if (filters.scienceOperator && filters.scienceValue !== null) {
      const sci = planet.sciencePerTurn
      const val = filters.scienceValue
      if (filters.scienceOperator === '>' && sci <= val) return false
      if (filters.scienceOperator === '=' && sci !== val) return false
      if (filters.scienceOperator === '<' && sci >= val) return false
    }

    // Distance filter
    if (filters.distancePlanetId > 0 && filters.distanceValue !== null && filters.distanceValue > 0) {
      const originPlanet = planetStore.planets.get(filters.distancePlanetId)
      if (originPlanet) {
        const distance = calculateDistance(
          originPlanet.xPos,
          originPlanet.yPos,
          planet.xPos,
          planet.yPos
        )
        if (distance > filters.distanceValue) return false
      }
    }

    return true
  })
})

// Reset all filters
const resetFilters = () => {
  filters.compatibilityMode = 'is'
  filters.compatibility = ''
  filters.isProducingMode = 'is'
  filters.isProducing = ''
  filters.planetGroupMode = 'is'
  filters.planetGroup = 0
  filters.hasBuildingMode = 'has'
  filters.hasBuilding = ''
  filters.planetSizeMode = 'is'
  filters.planetSize = 0
  filters.planetTypeMode = 'is'
  filters.planetType = 0
  filters.populationOperator = ''
  filters.populationValue = null
  filters.foodOperator = ''
  filters.foodValue = null
  filters.productionOperator = ''
  filters.productionValue = null
  filters.scienceOperator = ''
  filters.scienceValue = null
  filters.distancePlanetId = 0
  filters.distanceValue = null
}

// Apply selection
const applySelection = () => {
  tableStore.planets.selectedRowIds = filteredPlanets.value.map((planet) => planet.id)
  tableStore.planets.selectedAll = false
  gameStore.modalType = ''
}

// Cancel
const cancel = () => {
  gameStore.modalType = ''
}

// Compatibility options for SelectSearch
const compatibilityOptions = computed(() => [
  { value: '', label: t('modals.advancedSelect.options.any') },
  { value: 'A', label: t('modals.advancedSelect.compatibilityValues.paradise') },
  { value: 'B', label: t('modals.advancedSelect.compatibilityValues.lush') },
  { value: 'C', label: t('modals.advancedSelect.compatibilityValues.habitable') },
  { value: 'D', label: t('modals.advancedSelect.compatibilityValues.incompatible') },
  { value: 'E', label: t('modals.advancedSelect.compatibilityValues.toxic') }
])

// Is Producing options for SelectSearch
const isProducingOptions = computed(() => {
  return [
    { value: '', label: t('modals.advancedSelect.options.any') },
    { value: 'none', label: t('modals.advancedSelect.options.nothing') },
    ...productionItems.value.map((item) => ({
      value: item.key,
      label: item.name
    }))
  ]
})

// Planet Group options for SelectSearch
const planetGroupOptions = computed(() => {
  return [
    { value: 0, label: t('modals.advancedSelect.options.any') },
    { value: -1, label: t('modals.advancedSelect.options.notInGroup') },
    ...[...planetGroupStore.planetGroups.values()]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((group) => ({
        value: group.id,
        label: group.name
      }))
  ]
})

// Building options formatted for SelectSearch
const buildingOptions = computed(() => {
  return [
    { value: '', label: t('modals.advancedSelect.options.any') },
    ...availableBuildings.value.map((building) => ({
      value: building,
      label: t(`buildings.buildings.${building}.name`)
    }))
  ]
})

// Planet size options for SelectSearch
const planetSizeOptions = computed(() => {
  const sizes = new Set<number>()
  for (const planet of playerPlanets.value) {
    sizes.add(planet.size)
  }
  const sizeLabels: Record<number, string> = {
    1: t('modals.advancedSelect.sizes.small'),
    2: t('modals.advancedSelect.sizes.medium'),
    3: t('modals.advancedSelect.sizes.large')
  }
  return [
    { value: 0, label: t('modals.advancedSelect.options.any') },
    ...Array.from(sizes)
      .sort((a, b) => a - b)
      .map((size) => ({
        value: size,
        label: sizeLabels[size] || `Size ${size}`
      }))
  ]
})

// Planet type options for SelectSearch
const planetTypeOptions = computed(() => {
  const types = new Set<number>()
  for (const planet of playerPlanets.value) {
    types.add(Number(planet.type))
  }
  return [
    { value: 0, label: t('modals.advancedSelect.options.any') },
    ...Array.from(types)
      .sort((a, b) => a - b)
      .map((type) => {
        const planetType = assetStore.planetTypes.get(type)
        return {
          value: type,
          label: planetType?.name || `Type ${type}`
        }
      })
  ]
})

// Planet options for distance filter SelectSearch
const distancePlanetOptions = computed(() => {
  return [
    { value: 0, label: t('modals.advancedSelect.options.none') },
    ...playerPlanets.value
      .slice()
      .sort((a, b) => a.planetName.localeCompare(b.planetName))
      .map((planet) => ({
        value: planet.id,
        label: planet.planetName
      }))
  ]
})

// Calculate distance between two planets
const calculateDistance = (x1: number, y1: number, x2: number, y2: number): number => {
  return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2))
}

</script>

<template>
  <SmallModel :title="t('modals.advancedSelect.title')">
    <div class="p-4">
      <!-- Filters -->
      <div class="space-y-3">
        <!-- Compatibility Filter -->
        <div>
          <label class="block text-sm text-metal-400 pb-2"><GenericIcon type="planet" class="mr-2" />{{ t('modals.advancedSelect.compatibility') }}</label>
          <div class="flex gap-2">
            <FormSelect
              v-model="filters.compatibilityMode"
              :options="isIsNotOptions"
              class="w-24"
            />
            <div class="flex-1">
              <SelectSearch
                v-model="filters.compatibility"
                :options="compatibilityOptions"
                :placeholder="t('common.placeholders.any')"
              />
            </div>
          </div>
        </div>

        <!-- Is Producing Filter -->
        <div>
          <label class="block text-sm text-metal-400 pb-2"><GenericIcon type="production" class="mr-2" />{{ t('modals.advancedSelect.producing') }}</label>
          <div class="flex gap-2">
            <FormSelect
              v-model="filters.isProducingMode"
              :options="isIsNotOptions"
              class="w-24"
            />
            <div class="flex-1">
              <SelectSearch
                v-model="filters.isProducing"
                :options="isProducingOptions"
                :placeholder="t('common.placeholders.any')"
              />
            </div>
          </div>
        </div>

        <!-- Planet Group Filter -->
        <div>
          <label class="block text-sm text-metal-400 pb-2"><GenericIcon type="planet" class="mr-2" />{{ t('modals.advancedSelect.planetGroup') }}</label>
          <div class="flex gap-2">
            <FormSelect
              v-model="filters.planetGroupMode"
              :options="isIsNotOptions"
              class="w-24"
            />
            <div class="flex-1">
              <SelectSearch
                v-model="filters.planetGroup"
                :options="planetGroupOptions"
                :placeholder="t('common.placeholders.any')"
              />
            </div>
          </div>
        </div>

        <!-- Building Filter -->
        <div>
          <label class="block text-sm text-metal-400 pb-2"><GenericIcon type="production" class="mr-2" />{{ t('modals.advancedSelect.building') }}</label>
          <div class="flex gap-2">
            <FormSelect
              v-model="filters.hasBuildingMode"
              :options="hasHasNotOptions"
              class="w-24"
            />
            <div class="flex-1">
              <SelectSearch
                v-model="filters.hasBuilding"
                :options="buildingOptions"
                :placeholder="t('common.placeholders.any')"
              />
            </div>
          </div>
        </div>

        <!-- Planet Size & Type Filters -->
        <div class="flex gap-4">
          <!-- Planet Size Filter -->
          <div class="flex-1">
            <label class="block text-sm text-metal-400 pb-2"><GenericIcon type="planet" class="mr-2" />{{ t('modals.advancedSelect.planetSize') }}</label>
            <div class="flex gap-2">
              <FormSelect
                v-model="filters.planetSizeMode"
                :options="isIsNotOptions"
                class="w-16"
              />
              <div class="flex-1">
                <SelectSearch
                  v-model="filters.planetSize"
                  :options="planetSizeOptions"
                  :placeholder="t('common.placeholders.any')"
                />
              </div>
            </div>
          </div>
          <!-- Planet Type Filter -->
          <div class="flex-1">
            <label class="block text-sm text-metal-400 pb-2"><GenericIcon type="planet" class="mr-2" />{{ t('modals.advancedSelect.planetType') }}</label>
            <div class="flex gap-2">
              <FormSelect
                v-model="filters.planetTypeMode"
                :options="isIsNotOptions"
                class="w-16"
              />
              <div class="flex-1">
                <SelectSearch
                  v-model="filters.planetType"
                  :options="planetTypeOptions"
                  :placeholder="t('common.placeholders.any')"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Population & Food Filters -->
        <div class="flex gap-4">
          <!-- Population Filter -->
          <div class="flex-1">
            <label class="block text-sm text-metal-400 pb-2"><GenericIcon type="person" class="mr-2" />{{ t('modals.advancedSelect.population') }}</label>
            <div class="flex gap-2">
              <FormSelect
                v-model="filters.populationOperator"
                :options="numericOperatorOptions"
                class="w-16"
              />
              <FormInput
                v-model="filters.populationValue"
                type="number"
                min="0"
                :placeholder="t('common.placeholders.value')"
                class="flex-1"
              />
            </div>
          </div>
          <!-- Food Filter -->
          <div class="flex-1">
            <label class="block text-sm text-metal-400 pb-2"><GenericIcon type="food" class="mr-2" />{{ t('modals.advancedSelect.food') }}</label>
            <div class="flex gap-2">
              <FormSelect
                v-model="filters.foodOperator"
                :options="numericOperatorOptions"
                class="w-16"
              />
              <FormInput
                v-model="filters.foodValue"
                type="number"
                min="0"
                :placeholder="t('common.placeholders.value')"
                class="flex-1"
              />
            </div>
          </div>
        </div>

        <!-- Production & Science Filters -->
        <div class="flex gap-4">
          <!-- Production Filter -->
          <div class="flex-1">
            <label class="block text-sm text-metal-400 pb-2"><GenericIcon type="production" class="mr-2" />{{ t('modals.advancedSelect.production') }}</label>
            <div class="flex gap-2">
              <FormSelect
                v-model="filters.productionOperator"
                :options="numericOperatorOptions"
                class="w-16"
              />
              <FormInput
                v-model="filters.productionValue"
                type="number"
                min="0"
                :placeholder="t('common.placeholders.value')"
                class="flex-1"
              />
            </div>
          </div>
          <!-- Science Filter -->
          <div class="flex-1">
            <label class="block text-sm text-metal-400 pb-2"><GenericIcon type="science" class="mr-2" />{{ t('modals.advancedSelect.science') }}</label>
            <div class="flex gap-2">
              <FormSelect
                v-model="filters.scienceOperator"
                :options="numericOperatorOptions"
                class="w-16"
              />
              <FormInput
                v-model="filters.scienceValue"
                type="number"
                min="0"
                :placeholder="t('common.placeholders.value')"
                class="flex-1"
              />
            </div>
          </div>
        </div>

        <!-- Distance Filter -->
        <div>
          <label class="block text-sm text-metal-400 pb-2"><GenericIcon type="location" class="mr-2" />{{ t('modals.advancedSelect.distanceFromPlanet') }}</label>
          <div class="flex gap-2">
            <div class="flex-1">
              <SelectSearch
                v-model="filters.distancePlanetId"
                :options="distancePlanetOptions"
                :placeholder="t('modals.planetAdvancedSelect.selectPlanetPlaceholder')"
              />
            </div>
            <FormInput
              v-model="filters.distanceValue"
              type="number"
              min="0"
              :placeholder="t('modals.planetAdvancedSelect.distancePlaceholder')"
              class="w-24"
            />
          </div>
        </div>
      </div>

      <!-- Planet Count -->
      <div class="mt-4 py-2 text-center border-t border-metal-700">
        <span class="text-metal-400">{{ t('modals.advancedSelect.planetsMatching') }} </span>
        <span class="text-white font-semibold">{{ filteredPlanets.length }}</span>
        <span class="text-metal-500"> / {{ playerPlanets.length }}</span>
      </div>

      <!-- Buttons -->
      <div class="flex justify-between gap-2 mt-2">
        <ActionButton @click="cancel">{{ t('common.actions.cancel') }}</ActionButton>
        <ActionButton @click="resetFilters">{{ t('common.actions.reset') }}</ActionButton>
        <ActionButton @click="applySelection">{{ t('common.actions.select') }}</ActionButton>
      </div>
    </div>
  </SmallModel>
</template>

<style scoped lang="scss"></style>
